import './styles.css';
import type { Session } from '@supabase/supabase-js';
import { configured } from './config';
import { sb } from './lib/supabase';
import { renderSignIn } from './auth/signin';
import { SHELL } from './app/shell';
import { loadBoot, makeEnv, offlineBoot } from './app/env';
// @ts-expect-error: plain JavaScript (the screens, src/app/app.js)
import { startApp } from './app/app.js';
import { renderAdmin } from './admin/admin';

// The owner's dashboard (v2, 13 Oct 2026): /admin, only for accounts in app_admins.
const ADMIN = location.pathname.replace(/\/+$/, '') === '/admin';

const root = document.getElementById('app')!;

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

let running: { stop(): void } | null = null;
/** The Google display name for the sidebar (redesign), or '' to fall back to the email. */
const displayName = (s: Session) => String((s.user.user_metadata as Record<string, unknown> | undefined)?.full_name || (s.user.user_metadata as Record<string, unknown> | undefined)?.name || '');

async function showApp(s: Session) {
  if (ADMIN) { void renderAdmin(root); return; }
  root.innerHTML = '<div class="boot">Loading…</div>';
  // Notes that this person opened the app today (one row per day, for the owner's user counts).
  void sb().rpc('note_active').then(() => {}, () => {});
  let boot;
  try { boot = await Promise.race([loadBoot(), new Promise<never>((_, rej) => setTimeout(() => rej(new Error('The server took too long to answer.')), 10000))]); }
  catch (e) {
    // Offline (or the server is down): open the copy kept on this device, if there is one.
    const kept = offlineBoot(s.user.id);
    if (kept) {
      root.innerHTML = SHELL;
      running = startApp(Object.assign(makeEnv(s.user.id, s.user.email ?? '', kept), { name: displayName(s) }));
      showOfflineNote();
      return;
    }
    root.innerHTML = `<main class="auth"><section class="panel auth-card"><h1>Portfolio Ledger</h1><p class="dim">Couldn’t load your ledger: ${String((e as Error).message).replace(/</g, '&lt;')}</p><button class="btn" onclick="location.reload()">Try again</button></section></main>`;
    return;
  }
  root.innerHTML = SHELL;
  running = startApp(Object.assign(makeEnv(s.user.id, s.user.email ?? '', boot), { name: displayName(s) }));
}

if (!configured()) {
  root.innerHTML = '<main class="auth"><section class="panel auth-card"><h1>Portfolio Ledger</h1><p class="dim">This build has no server settings yet.</p></section></main>';
} else {
  // A failed Google sign-in comes back with the reason in the address.
  const q = new URLSearchParams(location.search + '&' + location.hash.slice(1));
  const startError = q.get('error_description') ? 'Sign-in didn’t finish. Please try again.' : '';
  if (startError) history.replaceState(null, '', location.pathname);

  let shown: string | null = null;
  const show = (s: Session | null) => {
    const id = s ? s.user.id : '';
    if (shown === id) return;
    shown = id;
    if (running) { running.stop(); running = null; }
    if (s) void showApp(s);
    else renderSignIn(root, startError);
  };
  sb().auth.onAuthStateChange((_event, s) => show(s));
  void sb().auth.getSession().then(({ data }) => show(data.session));
}

/** A note while the app runs from this device's copy. It goes away once the connection is back and
 *  the app has saved (the app's own status line shows that). */
function showOfflineNote() {
  const n = document.createElement('div');
  n.className = 'offline-note';
  n.setAttribute('role', 'status');
  n.textContent = navigator.onLine ? 'Couldn’t reach the server. Showing the copy saved on this device; changes are saved once it’s reachable again.'
    : 'You’re offline. Showing the copy saved on this device; changes are saved when you’re back online.';
  document.body.appendChild(n);
  addEventListener('online', () => setTimeout(() => n.remove(), 4000), { once: true });
}
